import useTranslate from "@hooks/use_translate"
import {
  Button,
  Chip,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from "@mui/material"
import {
  MigrationRow,
  RISK_COLOR,
  RUN_STATUS_COLOR,
  STATUS_COLOR,
  formatElapsed,
  isSeedSpec,
  pickText
} from "./migration.models"

const mono = { fontFamily: "monospace", fontSize: 12 }

type Props = {
  rows: MigrationRow[]
  now: number
  onOpen: (row: MigrationRow) => void
  /** The open row, or the last one opened once the drawer is closed. */
  highlightedType?: string | null
}

function StatusCell({ row }: { row: MigrationRow }) {
  const { translate } = useTranslate()
  if (!row.state) {
    return (
      <Chip
        size="small"
        variant="outlined"
        label={translate("migration_status_CHECKING")}
      />
    )
  }
  const { status } = row.state
  return (
    <Tooltip title={translate(`migration_status_help_${status}`)}>
      <Chip
        size="small"
        label={translate(`migration_status_${status}`)}
        color={STATUS_COLOR[status]}
      />
    </Tooltip>
  )
}

function AffectedCell({ row }: { row: MigrationRow }) {
  const { translate } = useTranslate()
  const { state, spec } = row
  if (!state) return <Skeleton width={64} sx={{ ml: "auto" }} />
  if (state.probeError) return <>—</>
  if (!isSeedSpec(spec)) {
    return (
      <>{`${state.affectedCount.toLocaleString()} / ${state.totalCount.toLocaleString()}`}</>
    )
  }
  return (
    <Tooltip title={translate("migration_seed_affected_help")}>
      <span>{`${state.affectedCount} / ${spec.expectedDocuments.count}`}</span>
    </Tooltip>
  )
}

function LastRunCell({ row, now }: { row: MigrationRow; now: number }) {
  const { translate } = useTranslate()
  const run = row.lastRun
  if (!run) {
    return (
      <Typography variant="caption" color="text.secondary">
        {translate("migration_no_panel_run")}
      </Typography>
    )
  }
  if (run.status === "RUNNING") {
    return (
      <Stack spacing={0.5} sx={{ minWidth: 120 }}>
        <Typography variant="caption" color="info.main">
          {translate("migration_run_status_RUNNING")} ·{" "}
          {formatElapsed(now - new Date(run.startedAt).getTime())}
        </Typography>
        <LinearProgress />
      </Stack>
    )
  }
  return (
    <Stack spacing={0.25}>
      <Typography variant="caption">
        {new Date(run.startedAt).toLocaleString()}
      </Typography>
      <Typography
        variant="caption"
        color={`${RUN_STATUS_COLOR[run.status]}.main`}
      >
        {translate(`migration_run_status_${run.status}`)}
        {run.durationMs != null && ` · ${formatElapsed(run.durationMs)}`}
      </Typography>
    </Stack>
  )
}

export default function MigrationTable({
  rows,
  now,
  onOpen,
  highlightedType
}: Props) {
  const { translate, currentLanguage } = useTranslate()

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell width={48}>
              <Tooltip title={translate("migration_order_help")}>
                <span>{translate("migration_col_order")}</span>
              </Tooltip>
            </TableCell>
            <TableCell>{translate("migration_col_status")}</TableCell>
            <TableCell>{translate("migration_col_migration")}</TableCell>
            <TableCell>{translate("migration_col_collections")}</TableCell>
            <TableCell align="right">
              {translate("migration_col_affected")}
            </TableCell>
            <TableCell>{translate("migration_col_risk")}</TableCell>
            <TableCell>{translate("migration_col_last_run")}</TableCell>
            <TableCell align="right" />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow
              key={row.spec.type}
              hover
              selected={row.spec.type === highlightedType}
              sx={{
                "&.Mui-selected": {
                  boxShadow: (theme) =>
                    `inset 4px 0 0 ${theme.palette.primary.main}`
                }
              }}
            >
              <TableCell sx={mono}>{row.order}</TableCell>
              <TableCell>
                <StatusCell row={row} />
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {pickText(row.spec.title, currentLanguage)}
                </Typography>
                <Typography variant="caption" sx={mono} color="text.secondary">
                  {row.spec.type}
                </Typography>
              </TableCell>
              <TableCell sx={mono}>{row.spec.collection}</TableCell>
              <TableCell align="right" sx={mono}>
                <AffectedCell row={row} />
              </TableCell>
              <TableCell>
                <Chip
                  size="small"
                  variant="outlined"
                  label={translate(`migration_risk_${row.spec.risk}`)}
                  color={RISK_COLOR[row.spec.risk]}
                />
              </TableCell>
              <TableCell>
                <LastRunCell row={row} now={now} />
              </TableCell>
              <TableCell align="right">
                <Button
                  size="small"
                  disabled={!row.state}
                  onClick={() => onOpen(row)}
                >
                  {translate("migration_details_button")}
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )
}
